export type PartnerWorkspaceTabName = 'Home' | 'Clients' | 'Work' | 'Inbox' | 'More';

export interface PartnerWorkspaceTab {
  name: PartnerWorkspaceTabName;
  label: string;
  icon: string;
  sidebarIcon: [string, string];
}

// One registry drives both the mobile tab bar and desktop sidebar. Keeping the
// taxonomy here prevents the two shells from drifting into different products.
export const PARTNER_WORKSPACE_TABS: readonly PartnerWorkspaceTab[] = [
  { name: 'Home', label: 'Home', icon: 'house-heart', sidebarIcon: ['fad', 'house-heart'] },
  { name: 'Clients', label: 'Clients', icon: 'users', sidebarIcon: ['fad', 'users'] },
  { name: 'Work', label: 'Work', icon: 'inbox', sidebarIcon: ['fad', 'inbox'] },
  { name: 'Inbox', label: 'Inbox', icon: 'comments', sidebarIcon: ['fad', 'comments'] },
  { name: 'More', label: 'More', icon: 'ellipsis', sidebarIcon: ['fas', 'ellipsis'] },
] as const;

export interface PartnerCapabilityRoute {
  tab: PartnerWorkspaceTabName;
  screen: string;
}

// Regression contract for every capability that existed in the old partner
// shell. Screens may move, but removing a route requires an intentional update
// to this map and its navigation coverage test.
export const PARTNER_CAPABILITY_ROUTES: Record<string, PartnerCapabilityRoute> = {
  clients: { tab: 'Clients', screen: 'PartnerCustomers' },
  clientPlans: { tab: 'Clients', screen: 'PartnerProjects' },
  fundingPlanner: { tab: 'Clients', screen: 'ANPPlanner' },
  fundingSimulations: { tab: 'Clients', screen: 'ANPSimulations' },
  intakes: { tab: 'Work', screen: 'PartnerIntakes' },
  invitations: { tab: 'Work', screen: 'PartnerInvites' },
  messages: { tab: 'Inbox', screen: 'PartnerMessages' },
  clientDocuments: { tab: 'More', screen: 'PartnerDocsRoot' },
  team: { tab: 'More', screen: 'PartnerMembers' },
  insights: { tab: 'More', screen: 'PartnerInsights' },
  accountAssistant: { tab: 'More', screen: 'PartnerEllie' },
  partnerSettings: { tab: 'More', screen: 'PartnerSettings' },
  support: { tab: 'More', screen: 'PartnerSupportChat' },
};

const OUTSTANDING_INVITATION_STATUSES = new Set(['sent', 'resent', 'registered']);

export function getPartnerWorkSummary(invites: readonly any[] | null | undefined) {
  let pendingIntakes = 0;
  let outstandingInvitations = 0;

  for (const item of invites || []) {
    if (item?.status === 'pending') pendingIntakes += 1;
    else if (OUTSTANDING_INVITATION_STATUSES.has(item?.status)) outstandingInvitations += 1;
  }

  return {
    pendingIntakes,
    outstandingInvitations,
    totalAttention: pendingIntakes + outstandingInvitations,
  };
}

function customerActivityTime(customer: any): number {
  const raw = customer?.last_activity_at || customer?.updated_at || customer?.created_at;
  const parsed = raw ? new Date(raw).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
}

export function sortRecentPartnerCustomers(customers: readonly any[] | null | undefined, limit = 3): any[] {
  return [...(customers || [])]
    .sort((a, b) => customerActivityTime(b) - customerActivityTime(a))
    .slice(0, Math.max(0, limit));
}
